// Appsignal attempts to serialize turbo frame missing errors,
// resulting in only `{}` being reported.
// Instead intercept the error and send a custom error with useful info.
export function installTurboFrameMissingListener(appsignal) {
  document.addEventListener('turbo:frame-missing', (event) => {
    try {
      const status = event.detail.response?.status;
      const frame = event.target;
      const isTurboFrame = frame.tagName?.toLowerCase() === 'turbo-frame';

      // When a turbo-frame is removed from the DOM while its request is still
      // in flight (e.g. user navigates away or closes an offcanvas), Turbo
      // dispatches the event on document.documentElement instead of the
      // original frame. This is a benign race condition, not a real error.
      if (!isTurboFrame) return;

      const frameId = frame.id || 'unknown';

      // If frame has a pre-rendered error state, toggle visibility and return
      const errorState = frame.querySelector('[data-turbo-frame-error-state]');
      if (errorState) {
        frame
          .querySelector('[data-turbo-frame-loading-state]')
          ?.classList.add('d-none');
        errorState.classList.remove('d-none');
        event.preventDefault();
        return;
      }

      // If the frame request itself failed (e.g. edge/origin returns 503),
      // there's no real "missing frame" bug in our templates to fix.
      // Avoid noisy AppSignal errors that mask the actual upstream problem.
      if (status === 503) {
        frame.innerHTML = `<strong class="turbo-frame-error">Content missing</strong>`;
        event.preventDefault();
        return;
      }

      // Listener is installed even when AppSignal is unavailable (no key),
      // so only report when the client exists. Always show the fallback UI.
      if (appsignal) {
        const span = appsignal.createSpan();

        const error = new Error(`Frame "${frameId}" not found (${status})`);
        error.name = 'MissingTurboFrame';
        span.setError(error);

        span.setTags({
          turbo_frame_id: frameId,
          request_url: window.location.href,
          response_url: event.detail.response?.url,
          status,
          // The !isTurboFrame guard above catches one detachment path (Turbo
          // redirects to documentElement). These tags catch a second: a page
          // replacement swaps the <body> while a FrameController still holds
          // a reference to the old element — tagName stays 'turbo-frame' so
          // the guard passes, but isConnected is false.
          frame_connected: String(frame.isConnected),
          frame_in_dom: String(document.getElementById(frameId) === frame),
          offcanvas_visible: String(
            document
              .getElementById('turbo-offcanvas')
              ?.classList.contains('show') ?? false
          ),
        });

        span.setAction('TurboFrameMissingError');
        span.setNamespace('turbo-frame');

        appsignal.send(span);
      }

      frame.innerHTML = `<strong class="turbo-frame-error">Content missing</strong>`;
      event.preventDefault();
    } catch (e) {
      console.error('[AppSignal] Failed to handle turbo:frame-missing:', e);
    }
  });
}
